import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search,
    Eye,
    Check,
    X,
    ThumbsUp,
    ThumbsDown,
    Clock,
    User,
    Briefcase,
    Calendar,
    Building2,
    GraduationCap,
    Star,
    Sparkles,
    Ban,
    History,
    Link as LinkIcon,
    MapPin,
    RefreshCw,
    Download,
} from "lucide-react";
import "./TraitementCandidature.css";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";
import {
    fetchCandidaturesRequest,
    validerCandidatureRequest,
    rejeterCandidatureRequest,
    annulerCandidatureRequest,
} from "../../../../redux/actions/candidaturesActions";

import {
    fetchEntretiensByCandidatureRequest,
    createEntretienRequest,
    sendInterviewCalendarRequest,
    updateEntretienRequest,
} from "../../../../redux/actions/entretiensActions";

import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { fetchCampagnesRequest } from "../../../../redux/actions/campagnesActions";

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const NIVEAU_ETUDES_LABELS = {
    DIPLOME_BAC_2: "Diplôme (Bac + 2)",
    LICENCE_BAC_3: "Licence (Bac + 3)",
    MASTER_BAC_5: "Master (Bac + 5)",
    INGENIEUR_BAC_5: "Ingénieur (Bac + 5)",
    DOCTORAT_ET_PLUS: "Doctorat et plus",
};

const DISPLAY_STATUS = {
    "en attente": "En attente",
    "validée": "Approuvée",
    "rejetée": "Rejetée",
    "annulée": "Annulée",
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const hasCurrentUserAsEvaluationIntervenant = (candidature, currentUserId) =>
    (candidature?.evaluationIntervenants || []).some(
        (intervenant) => Number(intervenant?.intervenantId) === Number(currentUserId)
    );

const getDisplayStatus = (value) => DISPLAY_STATUS[normalizeStatus(value)] || value || "—";

const getDisplayedPoste = (c) => {
    if (c?.autrePoste?.trim()) return c.autrePoste.trim();
    return c?.posteNom || "—";
};

const getDisplayedEtablissement = (c) => {
    if (c?.autreEtablissement?.trim()) return c.autreEtablissement.trim();
    return c?.etablissementNom || "—";
};

const getDisplayedNiveauEtude = (c) => {
    const raw = c?.niveauEtude;
    return NIVEAU_ETUDES_LABELS[raw] || raw || "—";
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("fr-FR");
};

const formatMode = (mode) => {
    if (mode === "EN_LIGNE") return "En ligne";
    if (mode === "PRESENTIEL") return "Présentiel";
    return mode || "—";
};

const normalizeApiBase = () => {
    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    if (/\/api$/i.test(base)) return base;
    return `${base}/api`;
};

const buildCvUrl = (filename) => {
    if (!filename) return "#";
    return `${normalizeApiBase()}/candidat-theque/cv/${encodeURIComponent(filename)}`;
};

const getFileNameFromStoredPath = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    try {
        const url = new URL(raw);
        const parts = decodeURIComponent(url.pathname).split(/[\\/]+/).filter(Boolean);
        return parts.pop() || "";
    } catch (error) {
        const clean = raw.split("?")[0].split("#")[0];
        const parts = decodeURIComponent(clean).split(/[\\/]+/).filter(Boolean);
        return parts.pop() || clean;
    }
};

const getCandidatureCvDocument = (candidature) =>
    getFileNameFromStoredPath(
        candidature?.cvDocument ?? candidature?.cv ?? candidature?.cvFileName ?? candidature?.cvUrl ?? ""
    );

const canResolveCandidateCv = (candidature) =>
    !!candidature?.id || !!getCandidatureCvDocument(candidature) || !!candidature?.candidatId;

const getFilenameFromContentDisposition = (header) => {
    const match = String(header || "").match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    return match ? decodeURIComponent(match[1].replace(/"/g, "")) : "";
};

const getUserDisplayNameById = (users, userId) => {
    if (!userId) return "—";

    const user = (users || []).find((u) => Number(u.id) === Number(userId));
    if (!user) return `ID ${userId}`;

    if (user.fullName && String(user.fullName).trim()) {
        return user.fullName;
    }

    const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return full || `ID ${userId}`;
};




const buildInterviewParticipantModes = (candidature, users, currentUserId, entretien = null) => {
    const modes = {};

    (entretien?.invitationParticipants || []).forEach((participant) => {
        if (participant?.userId) {
            modes[participant.userId] = participant.recipientType || "CC";
        }
    });

    if (!entretien) {
        if (currentUserId) {
            modes[currentUserId] = "CC";
        }
        if (candidature?.demandeurId) {
            modes[candidature.demandeurId] = modes[candidature.demandeurId] || "CC";
        }
    }

    const availableIds = new Set((users || []).map((user) => Number(user.id)));
    Object.keys(modes).forEach((key) => {
        if (!availableIds.has(Number(key))) {
            delete modes[key];
        }
    });

    return modes;
};

const extractParticipantPayload = (participantModes) => {
    const toParticipantIds = [];
    const ccParticipantIds = [];

    Object.entries(participantModes || {}).forEach(([userId, mode]) => {
        if (mode === "TO") {
            toParticipantIds.push(Number(userId));
        }
        if (mode === "CC") {
            ccParticipantIds.push(Number(userId));
        }
    });

    return { toParticipantIds, ccParticipantIds };
};

/* =============================================
   MODAL GÉNÉRIQUE
============================================= */
const Modal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="tcModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="tcModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcModal__header">
                    <div className="tcModal__title">{title}</div>
                    <button className="tcIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>
                <div className="tcModal__body">{children}</div>
                {footer && <div className="tcModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/* =============================================
   MODAL : Historique des entretiens
============================================= */
function HistoriqueEntretiensModal({ open, onClose, candidature, entretiens, users, onCalendarAction }) {
    if (!open || !candidature) return null;

    const interviews = entretiens || [];
    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="tcSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <History size={16} />
                        Historique des entretiens
                        <span className="tcSubModal__sep">-</span>
                        <span className="tcSubModal__candidate">
                            {candidature.candidatPrenom} {candidature.candidatNom}
                        </span>
                    </div>
                    <button className="tcIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__body">
                    {interviews.length === 0 ? (
                        <div className="tcEmptyCell">Aucun entretien planifie</div>
                    ) : (
                        <div className="tcHistoryCards">
                            {interviews.map((e) => {
                                const isCancelled = String(e.statut || "").toUpperCase() === "ANNULE";
                                const typeLabel = e.type === "RH" ? "Entretien RH" : "Entretien Technique";

                                return (
                                    <div key={e.id} className="tcHistoryCard">
                                        <div className="tcHistoryCard__header">
                                            <div className="tcHistoryCard__titleWrap">
                                                <div className="tcHistoryCard__title">{typeLabel}</div>
                                                <span className={`tcBadge ${isCancelled ? "tcBadge--cancelled" : "tcBadge--info"}`}>
                                                    {e.statut || "PLANIFIE"}
                                                </span>
                                            </div>
                                            <div className="tcHistoryCard__meta">
                                                Planifie par : {getUserDisplayNameById(users, e.createdBy)}
                                            </div>
                                        </div>

                                        <div className="tcHistoryCard__details">
                                            <div><strong>Mode :</strong> {formatMode(e.mode)}</div>
                                            <div>
                                                <strong>{e.mode === "EN_LIGNE" ? "Lien visio" : "Lieu"} :</strong>{" "}
                                                {e.lieu || "-"}
                                            </div>
                                            <div><strong>Date :</strong> {e.date ? formatDate(e.date) : "-"}</div>
                                            <div><strong>Heure :</strong> {e.heure || "-"}</div>
                                            <div><strong>Commentaire :</strong> {e.commentaire || "-"}</div>
                                            <div><strong>Participants :</strong> {e.invitationParticipants?.length || 0} selectionne(s)</div>
                                        </div>

                                        <div className="tcHistoryCard__actions">
                                            <span className="tcHistoryCard__actionsLabel">Actions</span>
                                            {!isCancelled && (
                                                <button
                                                    className="tcIconBtn tcIconBtn--calendar"
                                                    type="button"
                                                    title="Replanifier"
                                                    aria-label="Replanifier l'entretien"
                                                    onClick={() => onCalendarAction(e, "RESCHEDULE_INTERVIEW")}
                                                >
                                                    <RefreshCw size={16} />
                                                </button>
                                            )}
                                            {!isCancelled && (
                                                <button
                                                    className="tcIconBtn tcIconBtn--danger"
                                                    type="button"
                                                    title="Annuler"
                                                    aria-label="Annuler l'entretien"
                                                    onClick={() => onCalendarAction(e, "CANCEL_INTERVIEW")}
                                                >
                                                    <Ban size={16} />
                                                </button>
                                            )}
                                            {isCancelled && (
                                                <span className="tcHistoryCard__cancelled">Entretien annule</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="tcSubModal__footer">
                    <button className="tcBtn tcBtn--ghost" onClick={onClose} type="button">
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   MODAL : Planifier Entretien Technique
============================================= */
function PlanifierEntretienModal({ open, onClose, candidature, onSave, users, currentUserId }) {
    const [date, setDate] = useState("");
    const [heure, setHeure] = useState("");
    const [modeEntretien, setModeEntretien] = useState("");
    const [lieuEntretien, setLieuEntretien] = useState("");
    const [note, setNote] = useState("");
    const [aiPlan, setAiPlan] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [participantModes, setParticipantModes] = useState({});

    const reset = () => {
        setDate("");
        setHeure("");
        setModeEntretien("");
        setLieuEntretien("");
        setNote("");
        setAiPlan("");
        setAiLoading(false);
        setParticipantModes({});
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    useEffect(() => {
        if (!open || !candidature) return;
        setParticipantModes(buildInterviewParticipantModes(candidature, users, currentUserId));
    }, [open, candidature, users, currentUserId]);

    const handleSubmit = () => {
        if (!date || !heure || !modeEntretien || !lieuEntretien) return;
        const participantPayload = extractParticipantPayload(participantModes);
        onSave({
            type: "TECHNIQUE",
            mode: modeEntretien,
            lieu: lieuEntretien,
            date,
            heure,
            dureeMinutes: 60,
            commentaire: note,
            ...participantPayload,
        });
        reset();
        onClose();
    };

    const generatePlanIA = () => {
        if (!candidature) return;
        setAiLoading(true);

        const fullName = `${candidature.candidatPrenom || ""} ${candidature.candidatNom || ""}`.trim();
        const poste = getDisplayedPoste(candidature);
        const suggestedLieu =
            modeEntretien === "PRESENTIEL"
                ? (lieuEntretien || "Salle A — Siège social")
                : "—";
        const suggestedLien =
            modeEntretien === "EN_LIGNE"
                ? (lieuEntretien || "https://meet.google.com/...")
                : "—";
        const suggestedNote =
            note || "Préparer une revue du CV + 2 exercices techniques adaptés au poste.";

        const plan = [
            `Plan entretien technique — ${fullName} (${poste})`,
            ``,
            `1) Objectif`,
            `- Valider compétences techniques, méthodologie, communication, et adéquation au poste.`,
            ``,
            `2) Déroulé (45–60 min)`,
            `- 5 min : accueil + contexte du poste`,
            `- 10 min : parcours candidat + projets clés`,
            `- 25 min : questions techniques + mini-exercice (selon poste)`,
            `- 10 min : architecture / bonnes pratiques / debugging`,
            `- 5 min : questions candidat + prochaines étapes`,
            ``,
            `3) Questions suggérées`,
            `- Explique un projet dont tu es fier : contraintes, choix techniques, résultat.`,
            `- Comment tu débugges un bug en prod ?`,
            `- Donne un exemple de compromis performance vs qualité.`,
            ``,
            `4) Logistique`,
            `- Mode : ${formatMode(modeEntretien)}`,
            `- Lieu : ${suggestedLieu}`,
            `- Lien visio : ${suggestedLien}`,
            `- Note organisateur : ${suggestedNote}`,
        ].join("\n");

        setTimeout(() => {
            setAiPlan(plan);
            setAiLoading(false);
        }, 250);
    };

    if (!open || !candidature) return null;

    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={handleClose}>
            <div className="tcSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <Calendar size={16} />
                        Planifier un entretien technique
                        <span className="tcSubModal__sep">—</span>
                        <span className="tcSubModal__candidate">
                            {candidature.candidatPrenom} {candidature.candidatNom}
                        </span>
                    </div>
                    <button className="tcIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__typeBanner">
                    <Briefcase size={14} />
                    Entretien Technique
                </div>

                <div className="tcSubModal__body">
                    {aiPlan && (
                        <div className="tcAiPlanBox">
                            <div className="tcAiPlanBox__head">
                                <Sparkles size={16} />
                                Planification IA (suggestion)
                            </div>
                            <pre className="tcAiPlanBox__content">{aiPlan}</pre>
                        </div>
                    )}

                    <div className="tcPlanForm">
                        <div className="tcPlanRow">
                            <div className="tcPlanField">
                                <label>Date *</label>
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </div>
                            <div className="tcPlanField">
                                <label>Heure *</label>
                                <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} />
                            </div>
                        </div>

                        <div className="tcPlanField">
                            <label>Mode *</label>
                            <select
                                value={modeEntretien}
                                onChange={(e) => {
                                    setModeEntretien(e.target.value);
                                    setLieuEntretien("");
                                }}
                            >
                                <option value="">Sélectionner</option>
                                <option value="EN_LIGNE">En ligne</option>
                                <option value="PRESENTIEL">Présentiel</option>
                            </select>
                        </div>

                        {modeEntretien === "EN_LIGNE" && (
                            <div className="tcPlanField">
                                <label>Lien visio *</label>
                                <div className="tcInline">
                                    <LinkIcon size={14} className="tcMutedIcon" />
                                    <input
                                        type="url"
                                        placeholder="https://meet.google.com/..."
                                        value={lieuEntretien}
                                        onChange={(e) => setLieuEntretien(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {modeEntretien === "PRESENTIEL" && (
                            <div className="tcPlanField">
                                <label>Adresse / salle *</label>
                                <div className="tcInline">
                                    <MapPin size={14} className="tcMutedIcon" />
                                    <input
                                        type="text"
                                        placeholder="Ex : Salle A — Siège social"
                                        value={lieuEntretien}
                                        onChange={(e) => setLieuEntretien(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="tcPlanField">
                            <label>Note pour l'organisateur</label>
                            <textarea
                                rows={3}
                                placeholder="Instructions particulières..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>

                        <div className="tcPlanField">
                            <label>Destinataires calendrier</label>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", background: "#f9fafb", fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                                    <span>Participant</span>
                                    <span>Invitation</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>
                                            {candidature?.candidatPrenom} {candidature?.candidatNom}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{candidature?.candidatEmail || "Email non renseigné"}</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: "#204385" }}>TO (défaut)</div>
                                </div>
                                {(users || []).map((user) => (
                                    <div key={user.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{getUserDisplayNameById(users, user.id)}</div>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email || "Email non renseigné"}</div>
                                        </div>
                                        <select
                                            value={participantModes[user.id] || "NONE"}
                                            onChange={(e) =>
                                                setParticipantModes((prev) => {
                                                    const next = { ...prev };
                                                    if (e.target.value === "NONE") {
                                                        delete next[user.id];
                                                    } else {
                                                        next[user.id] = e.target.value;
                                                    }
                                                    return next;
                                                })
                                            }
                                        >
                                            <option value="NONE">Aucun</option>
                                            <option value="TO">TO</option>
                                            <option value="CC">CC</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tcSubModal__footer tcSubModal__footer--split">
                    <div className="tcFooterLeft">
                        <button
                            className="tcBtn tcBtn--ghost tcBtn--ai"
                            type="button"
                            onClick={generatePlanIA}
                            disabled={aiLoading}
                        >
                            <Sparkles size={14} />
                            {aiLoading ? "Génération..." : "Planifier entretien IA"}
                        </button>
                    </div>

                    <div className="tcFooterRight">
                        <button className="tcBtn tcBtn--ghost" onClick={handleClose} type="button">
                            Annuler
                        </button>
                        <button
                            className="tcBtn tcBtn--primary"
                            onClick={handleSubmit}
                            disabled={!date || !heure || !modeEntretien || !lieuEntretien}
                            type="button"
                        >
                            <Calendar size={14} />
                            Planifier
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function EntretienCalendarModal({ open, onClose, candidature, entretien, users, currentUserId, onSubmit, loading, result }) {
    const [participantModes, setParticipantModes] = useState({});

    useEffect(() => {
        if (!open || !candidature) return;
        setParticipantModes(buildInterviewParticipantModes(candidature, users, currentUserId, entretien));
    }, [open, candidature, users, currentUserId, entretien]);

    if (!open || !candidature || !entretien) return null;

    const actionMeta = {
        SEND_INITIAL_INVITATION: {
            title: "Envoyer l'invitation Outlook",
            cta: "Envoyer l'invitation",
        },
        RESCHEDULE_INTERVIEW: {
            title: "Envoyer la mise à jour Outlook",
            cta: "Envoyer la mise à jour",
        },
        CANCEL_INTERVIEW: {
            title: "Envoyer l'annulation Outlook",
            cta: "Envoyer l'annulation",
        },
    }[open.actionType] || { title: "Invitation Outlook", cta: "Envoyer" };

    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="tcSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <Calendar size={16} />
                        {actionMeta.title}
                    </div>
                    <button className="tcIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__body">
                    <div className="tcPlanForm">
                        <div className="tcPlanRow">
                            <div className="tcPlanField">
                                <label>Candidat</label>
                                <input value={`${candidature.candidatPrenom || ""} ${candidature.candidatNom || ""}`.trim()} disabled />
                            </div>
                            <div className="tcPlanField">
                                <label>Entretien</label>
                                <input value={`${entretien.type || "ENTRETIEN"} • ${entretien.date || "—"} ${entretien.heure || ""}`} disabled />
                            </div>
                        </div>

                        <div className="tcPlanField">
                            <label>Destinataires calendrier</label>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", background: "#f9fafb", fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                                    <span>Participant</span>
                                    <span>Invitation</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>
                                            {candidature?.candidatPrenom} {candidature?.candidatNom}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{candidature?.candidatEmail || "Email non renseigné"}</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: "#204385" }}>TO (défaut)</div>
                                </div>
                                {(users || []).map((user) => (
                                    <div key={user.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{getUserDisplayNameById(users, user.id)}</div>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email || "Email non renseigné"}</div>
                                        </div>
                                        <select
                                            value={participantModes[user.id] || "NONE"}
                                            onChange={(e) =>
                                                setParticipantModes((prev) => {
                                                    const next = { ...prev };
                                                    if (e.target.value === "NONE") {
                                                        delete next[user.id];
                                                    } else {
                                                        next[user.id] = e.target.value;
                                                    }
                                                    return next;
                                                })
                                            }
                                        >
                                            <option value="NONE">Aucun</option>
                                            <option value="TO">TO</option>
                                            <option value="CC">CC</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {result && result.entretienId === entretien.id && (
                            <div className="tcHint" style={{ color: "#374151", fontSize: 13 }}>
                                {result.totalSent} envoyé(s), {result.totalSkipped} ignoré(s), {result.totalFailed} échec(s).
                            </div>
                        )}
                    </div>
                </div>

                <div className="tcSubModal__footer">
                    <button className="tcBtn tcBtn--ghost" onClick={onClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcBtn tcBtn--primary"
                        type="button"
                        disabled={loading}
                        onClick={() => onSubmit(open.actionType, entretien, extractParticipantPayload(participantModes))}
                    >
                        <Calendar size={14} />
                        {loading ? "Envoi..." : actionMeta.cta}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   MODAL : Évaluer Candidat
============================================= */
function ReplanifierEntretienModal({ open, onClose, candidature, entretien, users, currentUserId, onSubmit, loading }) {
    const [date, setDate] = useState("");
    const [heure, setHeure] = useState("");
    const [modeEntretien, setModeEntretien] = useState("");
    const [lieuEntretien, setLieuEntretien] = useState("");
    const [note, setNote] = useState("");
    const [participantModes, setParticipantModes] = useState({});

    useEffect(() => {
        if (!open || !candidature || !entretien) return;
        setDate(entretien.date || "");
        setHeure(entretien.heure || "");
        setModeEntretien(entretien.mode || "");
        setLieuEntretien(entretien.lieu || "");
        setNote(entretien.commentaire || "");
        setParticipantModes(buildInterviewParticipantModes(candidature, users, currentUserId, entretien));
    }, [open, candidature, entretien, users, currentUserId]);

    if (!open || !candidature || !entretien) return null;

    const handleSubmit = () => {
        const participantPayload = extractParticipantPayload(participantModes);
        onSubmit("RESCHEDULE_INTERVIEW", entretien, {
            date,
            heure,
            mode: modeEntretien,
            lieu: lieuEntretien,
            dureeMinutes: entretien.dureeMinutes || 60,
            commentaire: note,
            ...participantPayload,
        });
    };

    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="tcSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <RefreshCw size={16} />
                        Replanifier l'entretien
                    </div>
                    <button className="tcIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__body">
                    <div className="tcPlanForm">
                        <div className="tcPlanRow">
                            <div className="tcPlanField">
                                <label>Date *</label>
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </div>
                            <div className="tcPlanField">
                                <label>Heure *</label>
                                <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} />
                            </div>
                        </div>

                        <div className="tcPlanField">
                            <label>Mode *</label>
                            <select value={modeEntretien} onChange={(e) => setModeEntretien(e.target.value)}>
                                <option value="">Sélectionner</option>
                                <option value="EN_LIGNE">En ligne</option>
                                <option value="PRESENTIEL">Présentiel</option>
                            </select>
                        </div>

                        <div className="tcPlanField">
                            <label>{modeEntretien === "EN_LIGNE" ? "Lien visio *" : "Adresse / salle *"}</label>
                            <input value={lieuEntretien} onChange={(e) => setLieuEntretien(e.target.value)} />
                        </div>

                        <div className="tcPlanField">
                            <label>Note</label>
                            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                        </div>

                        <div className="tcPlanField">
                            <label>Destinataires calendrier</label>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", background: "#f9fafb", fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                                    <span>Participant</span>
                                    <span>Invitation</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>
                                            {candidature?.candidatPrenom} {candidature?.candidatNom}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{candidature?.candidatEmail || "Email non renseigne"}</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: "#204385" }}>TO (defaut)</div>
                                </div>
                                {(users || []).map((user) => (
                                    <div key={user.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{getUserDisplayNameById(users, user.id)}</div>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email || "Email non renseigne"}</div>
                                        </div>
                                        <select
                                            value={participantModes[user.id] || "NONE"}
                                            onChange={(e) =>
                                                setParticipantModes((prev) => {
                                                    const next = { ...prev };
                                                    if (e.target.value === "NONE") {
                                                        delete next[user.id];
                                                    } else {
                                                        next[user.id] = e.target.value;
                                                    }
                                                    return next;
                                                })
                                            }
                                        >
                                            <option value="NONE">Aucun</option>
                                            <option value="TO">TO</option>
                                            <option value="CC">CC</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tcSubModal__footer">
                    <button className="tcBtn tcBtn--ghost" onClick={onClose} type="button">
                        Fermer
                    </button>
                    <button
                        className="tcBtn tcBtn--primary"
                        onClick={handleSubmit}
                        disabled={loading || !date || !heure || !modeEntretien || !lieuEntretien}
                        type="button"
                    >
                        <RefreshCw size={14} />
                        {loading ? "Envoi..." : "Replanifier"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AnnulerEntretienCalendarModal({ open, onClose, entretien, candidature, loading, onConfirm }) {
    if (!open || !entretien || !candidature) return null;

    return (
        <Modal
            open={!!open}
            title="Annuler l'entretien"
            onClose={onClose}
            footer={
                <>
                    <button className="tcBtn tcBtn--ghost" type="button" onClick={onClose}>
                        Retour
                    </button>
                    <button
                        className="tcBtn tcBtn--danger"
                        type="button"
                        onClick={() => onConfirm("CANCEL_INTERVIEW", entretien, {})}
                        disabled={loading}
                    >
                        {loading ? "Envoi..." : "Annuler et notifier"}
                    </button>
                </>
            }
        >
            <div className="tcConfirmContent">
                <p>
                    Voulez-vous annuler l'entretien de <strong>{candidature.candidatPrenom} {candidature.candidatNom}</strong> prevu le{" "}
                    <strong>{formatDate(entretien.date)}</strong> a <strong>{entretien.heure || "-"}</strong> ?
                </p>
                <p>Cette action enverra une annulation ICS Outlook aux destinataires deja enregistres.</p>
            </div>
        </Modal>
    );
}

function EvaluerModal({ open, onClose, candidature, onSave }) {
    const [note, setNote] = useState(0);
    const [hoverNote, setHoverNote] = useState(0);
    const [commentaire, setCommentaire] = useState("");
    const [decision, setDecision] = useState("");
    const [competences, setCompetences] = useState({
        technique: 0,
        communication: 0,
        aptitude: 0,
        motivation: 0,
    });

    const reset = () => {
        setNote(0);
        setHoverNote(0);
        setCommentaire("");
        setDecision("");
        setCompetences({ technique: 0, communication: 0, aptitude: 0, motivation: 0 });
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = () => {
        if (!note || !decision) return;
        onSave({ note, commentaire, decision, competences });
        reset();
        onClose();
    };

    if (!open || !candidature) return null;

    const criterias = [
        { key: "technique", label: "Compétences techniques" },
        { key: "communication", label: "Communication" },
        { key: "aptitude", label: "Aptitude au poste" },
        { key: "motivation", label: "Motivation" },
    ];

    const noteLabels = ["", "Insuffisant", "Passable", "Bien", "Très bien", "Excellent"];

    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={handleClose}>
            <div className="tcSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <Star size={16} />
                        Évaluation
                        <span className="tcSubModal__sep">—</span>
                        <span className="tcSubModal__candidate">
                            {candidature.candidatPrenom} {candidature.candidatNom}
                        </span>
                    </div>
                    <button className="tcIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__body">
                    <div className="tcEvalSection">
                        <div className="tcEvalSectionTitle">Note globale *</div>
                        <div className="tcStars">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`tcStar ${(hoverNote || note) >= star ? "tcStar--filled" : ""}`}
                                    onMouseEnter={() => setHoverNote(star)}
                                    onMouseLeave={() => setHoverNote(0)}
                                    onClick={() => setNote(star)}
                                >
                                    <Star size={28} />
                                </button>
                            ))}
                            {note > 0 && <span className="tcStarLabel">{noteLabels[note]}</span>}
                        </div>
                    </div>

                    <div className="tcEvalSection">
                        <div className="tcEvalSectionTitle">Évaluation par critères</div>
                        <div className="tcCriterias">
                            {criterias.map((c) => (
                                <div key={c.key} className="tcCriteria">
                                    <div className="tcCriteria__label">{c.label}</div>
                                    <div className="tcCriteria__stars">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                className={`tcStarSm ${
                                                    competences[c.key] >= star ? "tcStarSm--filled" : ""
                                                }`}
                                                onClick={() =>
                                                    setCompetences((prev) => ({ ...prev, [c.key]: star }))
                                                }
                                            >
                                                <Star size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="tcEvalSection">
                        <div className="tcEvalSectionTitle">Commentaire</div>
                        <textarea
                            className="tcEvalTextarea"
                            rows={3}
                            placeholder="Points forts, points d'amélioration..."
                            value={commentaire}
                            onChange={(e) => setCommentaire(e.target.value)}
                        />
                    </div>

                    <div className="tcEvalSection">
                        <div className="tcEvalSectionTitle">Décision *</div>
                        <div className="tcDecisions">
                            {[
                                { value: "Retenu", color: "#16a34a", label: "✅ Retenu" },
                                { value: "En réserve", color: "#d97706", label: "⏳ En réserve" },
                                { value: "Refusé", color: "#dc2626", label: "❌ Refusé" },
                            ].map((d) => (
                                <div
                                    key={d.value}
                                    className={`tcDecisionCard ${
                                        decision === d.value ? "tcDecisionCard--selected" : ""
                                    }`}
                                    style={
                                        decision === d.value
                                            ? { borderColor: d.color, background: `${d.color}12` }
                                            : {}
                                    }
                                    onClick={() => setDecision(d.value)}
                                >
                                    {d.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="tcSubModal__footer">
                    <button className="tcBtn tcBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcBtn tcBtn--primary"
                        onClick={handleSubmit}
                        disabled={!note || !decision}
                        type="button"
                    >
                        <Star size={14} />
                        Enregistrer l'évaluation
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   MODAL : Annuler avec motif
============================================= */
function AnnulerModal({ open, onClose, candidature, onConfirm }) {
    const [motif, setMotif] = useState("");

    useEffect(() => {
        if (!open) setMotif("");
    }, [open]);

    const handleClose = () => {
        setMotif("");
        onClose();
    };

    const handleSubmit = () => {
        if (!motif.trim()) return;
        onConfirm(motif.trim());
        setMotif("");
    };

    if (!open || !candidature) return null;

    return (
        <div className="tcSubModal" role="dialog" aria-modal="true" onMouseDown={handleClose}>
            <div className="tcSubModal__panel tcSubModal__panel--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcSubModal__header">
                    <div className="tcSubModal__title">
                        <Ban size={16} className="tcSubModal__title--cancel" />
                        Annuler
                        <span className="tcSubModal__sep">—</span>
                        <span className="tcSubModal__candidate">
                            {candidature.candidatPrenom} {candidature.candidatNom}
                        </span>
                    </div>
                    <button className="tcIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcSubModal__body">
                    <p className="tcCancelInfo">
                        Vous êtes sur le point d'annuler la candidature de{" "}
                        <strong>
                            {candidature.candidatPrenom} {candidature.candidatNom}
                        </strong>{" "}
                        pour le poste de <strong>{getDisplayedPoste(candidature)}</strong>.
                    </p>

                    <div className="tcPlanField" style={{ marginTop: 16 }}>
                        <label>Motif d'annulation *</label>
                        <textarea
                            rows={4}
                            placeholder="Veuillez préciser le motif d'annulation..."
                            value={motif}
                            onChange={(e) => setMotif(e.target.value)}
                        />
                    </div>
                </div>

                <div className="tcSubModal__footer">
                    <button className="tcBtn tcBtn--ghost" onClick={handleClose} type="button">
                        Retour
                    </button>
                    <button
                        className="tcBtn tcBtn--cancel"
                        onClick={handleSubmit}
                        disabled={!motif.trim()}
                        type="button"
                    >
                        <Ban size={14} />
                        Confirmer l'annulation
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   COMPOSANT PRINCIPAL
============================================= */
export default function TraitementCandidature() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const candidaturesState = useSelector((state) => state.candidatures || {});
    const candidaturesRedux = candidaturesState.candidatures || [];
    const loading = !!candidaturesState.loading;
    const error = candidaturesState.error;

    const entretiensState = useSelector((state) => state.entretiens || {});
    const entretiensByCandidature = entretiensState.entretiensByCandidature || {};
    const calendarResult = entretiensState.calendarResult || null;

    const userState = useSelector((state) => state.user || {});
    const users = userState.users || [];
    const currentUserId = Number(sessionStorage.getItem("uid") || 0);

    const [candidatures, setCandidatures] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    const [showViewModal, setShowViewModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showPlanifierModal, setShowPlanifierModal] = useState(false);
    const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
    const [showEvaluerModal, setShowEvaluerModal] = useState(false);
    const [showAnnulerModal, setShowAnnulerModal] = useState(false);
    const [replanifyEntretien, setReplanifyEntretien] = useState(null);
    const [cancelCalendarEntretien, setCancelCalendarEntretien] = useState(null);

    const [selectedCandidature, setSelectedCandidature] = useState(null);
    const [rejectMotif, setRejectMotif] = useState("");
    const campagnesState = useSelector((state) => state.campagnes || {});
    const campagnes = campagnesState.list || [];

    const getCampagneName = (c) => {
        if (c?.campagneNom) return c.campagneNom;

        const campagne = (campagnes || []).find(
            (camp) => Number(camp.id) === Number(c?.campagneId)
        );

        return campagne?.nom || "—";
    };

    const [filters, setFilters] = useState({
        campagne: "",
        poste: "",
        niveauEtude: "",
        etablissement: "",
        anneeExperience: "",
        typeEmploi: "",
    });

    useEffect(() => {
        dispatch(fetchCandidaturesRequest({ scope: "manager" }));
        dispatch(getAllUsersRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const approvedCandidatures = useMemo(
        () => (candidaturesRedux || []).filter((c) => normalizeStatus(c.statut) === "validée"),
        [candidaturesRedux]
    );

    useEffect(() => {
        approvedCandidatures.forEach((c) => {
            dispatch(fetchEntretiensByCandidatureRequest(c.id));
        });
    }, [dispatch, approvedCandidatures]);

    useEffect(() => {
        setCandidatures(candidaturesRedux || []);
    }, [candidaturesRedux]);

    useEffect(() => {
        const isAnyModalOpen =
            showViewModal ||
            showApproveModal ||
            showRejectModal ||
            showPlanifierModal ||
            showHistoriqueModal ||
            !!replanifyEntretien ||
            !!cancelCalendarEntretien ||
            showEvaluerModal ||
            showAnnulerModal;

        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [
        showViewModal,
        showApproveModal,
        showRejectModal,
        showPlanifierModal,
        showHistoriqueModal,
        replanifyEntretien,
        cancelCalendarEntretien,
        showEvaluerModal,
        showAnnulerModal,
    ]);

    const resetFilters = () =>
        setFilters({
            campagne: "",
            poste: "",
            niveauEtude: "",
            etablissement: "",
            anneeExperience: "",
            typeEmploi: "",
        });

    const handleFilterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

    const isAnyFilterFilled = Object.values(filters).some((val) => val !== "");

    const filterCandidatures = (data) => {
        let f = [...data];

        if (filters.campagne) {
            f = f.filter(
                (c) => Number(c.campagneId) === Number(filters.campagne)
            );
        }
        if (filters.poste) {
            f = f.filter((c) =>
                getDisplayedPoste(c).toLowerCase().includes(filters.poste.toLowerCase())
            );
        }

        if (filters.niveauEtude) {
            f = f.filter((c) => getDisplayedNiveauEtude(c) === filters.niveauEtude);
        }

        if (filters.etablissement) {
            f = f.filter((c) => getDisplayedEtablissement(c) === filters.etablissement);
        }

        if (filters.anneeExperience) {
            f = f.filter((c) => String(c.anneesExperience || "") === String(filters.anneeExperience));
        }

        if (filters.typeEmploi) {
            f = f.filter((c) => String(c.typeEmploi || "") === String(filters.typeEmploi));
        }

        return f;
    };

    const getCandidaturesByTab = (tabIndex) => {
        if (tabIndex === 0) {
            return candidatures.filter((c) => normalizeStatus(c.statut) === "en attente");
        }
        if (tabIndex === 1) {
            return candidatures.filter((c) => normalizeStatus(c.statut) === "validée");
        }
        return candidatures.filter((c) =>
            ["rejetée", "annulée"].includes(normalizeStatus(c.statut))
        );
    };

    const openView = (c) => {
        setSelectedCandidature(c);
        setShowViewModal(true);
    };

    const closeView = () => {
        setShowViewModal(false);
        setSelectedCandidature(null);
    };

    const openApprove = (c) => {
        setSelectedCandidature(c);
        setShowApproveModal(true);
    };

    const closeApprove = () => {
        setShowApproveModal(false);
        setSelectedCandidature(null);
    };

    const openReject = (c) => {
        setSelectedCandidature(c);
        setRejectMotif("");
        setShowRejectModal(true);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setSelectedCandidature(null);
        setRejectMotif("");
    };

    const openAnnuler = (c) => {
        setSelectedCandidature(c);
        setShowAnnulerModal(true);
    };

    const closeAnnuler = () => {
        setShowAnnulerModal(false);
        setSelectedCandidature(null);
    };

    const openPlanifier = (c) => {
        setSelectedCandidature(c);
        setShowPlanifierModal(true);
    };

    const closePlanifier = () => {
        setShowPlanifierModal(false);
        setSelectedCandidature(null);
    };

    const openHistorique = (c) => {
        setSelectedCandidature(c);
        setShowHistoriqueModal(true);
    };

    const closeHistorique = () => {
        setShowHistoriqueModal(false);
        setSelectedCandidature(null);
    };

    const openCalendarModal = (entretien, actionType) => {
        if (!selectedCandidature || !entretien) return;
        if (actionType === "RESCHEDULE_INTERVIEW") {
            setReplanifyEntretien(entretien);
            return;
        }
        if (actionType === "CANCEL_INTERVIEW") {
            setCancelCalendarEntretien(entretien);
        }
    };

    const closeCalendarModal = () => {
        setReplanifyEntretien(null);
        setCancelCalendarEntretien(null);
    };

    const openEvaluer = (c) => {
        if (!hasCurrentUserAsEvaluationIntervenant(c, currentUserId)) return;
        setSelectedCandidature(c);
        setShowEvaluerModal(true);
        navigate(`/recrutement/evaluations/candidatures/${c.id}/fiche`, { state: { viewMode: "MANAGER" } });
    };

    const closeEvaluer = () => {
        setShowEvaluerModal(false);
        setSelectedCandidature(null);
    };

    const confirmApprove = () => {
        if (!selectedCandidature?.id) return;
        dispatch(validerCandidatureRequest(selectedCandidature.id, { scope: "manager" }));
        closeApprove();
    };

    const confirmReject = () => {
        if (!selectedCandidature?.id) return;

        if (!rejectMotif.trim()) {
            showSwal({
                icon: "warning",
                text: "Veuillez saisir un motif de rejet",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        dispatch(rejeterCandidatureRequest(selectedCandidature.id, rejectMotif.trim(), { scope: "manager" }));
        closeReject();
    };

    const confirmAnnuler = (motif) => {
        if (!selectedCandidature?.id) return;
        dispatch(annulerCandidatureRequest(selectedCandidature.id, motif, { scope: "manager" }));
        closeAnnuler();
    };

    const handleDownloadCv = async (candidature) => {
        try {
            const cvDocument = getCandidatureCvDocument(candidature);

            if (!candidature?.id) {
                showSwal({
                    icon: "warning",
                    text: "Aucun CV disponible pour ce candidat",
                    timer: 1800,
                    showConfirmButton: false,
                });
                return;
            }

            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const selectedCompanyId = sessionStorage.getItem("selectedCompanyId");
            const response = await getBlobRequest({
                url: `${normalizeApiBase()}/candidatures/${candidature.id}/cv`,
                token,
                extraHeaders: {
                    uid,
                    "X-Tenant-Id": selectedCompanyId,
                },
            });

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download =
                getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
                cvDocument ||
                `cv-candidature-${candidature.id}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Erreur telechargement CV:", error);
            showSwal({
                icon: "error",
                text: "Impossible de telecharger le CV.",
                timer: 2200,
                showConfirmButton: false,
            });
        }
    };

    const handlePlanifierSave = (data) => {
        if (!selectedCandidature?.id) return;

        dispatch(
            createEntretienRequest({
                type: "TECHNIQUE",
                mode: data.mode,
                lieu: data.lieu,
                date: data.date,
                heure: data.heure,
                dureeMinutes: data.dureeMinutes,
                commentaire: data.commentaire,
                toParticipantIds: data.toParticipantIds || [],
                ccParticipantIds: data.ccParticipantIds || [],
                candidatureId: selectedCandidature.id,
            })
        );

        setTimeout(() => {
            dispatch(fetchEntretiensByCandidatureRequest(selectedCandidature.id));
        }, 300);
    };

    const handleInterviewCalendarSubmit = (actionType, entretien, payload) => {
        if (!entretien?.id) return;

        if (actionType === "RESCHEDULE_INTERVIEW") {
            dispatch(
                updateEntretienRequest(
                    entretien.id,
                    {
                        type: entretien.type,
                        mode: payload.mode,
                        lieu: payload.lieu,
                        date: payload.date,
                        heure: payload.heure,
                        dureeMinutes: payload.dureeMinutes,
                        commentaire: payload.commentaire,
                        toParticipantIds: payload.toParticipantIds || [],
                        ccParticipantIds: payload.ccParticipantIds || [],
                        candidatureId: selectedCandidature?.id,
                    },
                    {
                        toParticipantIds: payload.toParticipantIds || [],
                        ccParticipantIds: payload.ccParticipantIds || [],
                    }
                )
            );
            return;
        }

        dispatch(
            sendInterviewCalendarRequest(entretien.id, actionType, {
                toParticipantIds: payload?.toParticipantIds || [],
                ccParticipantIds: payload?.ccParticipantIds || [],
            })
        );
    };
 
    useEffect(() => {
        if (!calendarResult) return;
        if (replanifyEntretien && Number(calendarResult.entretienId) === Number(replanifyEntretien.id)) {
            setReplanifyEntretien(null);
        }
        if (cancelCalendarEntretien && Number(calendarResult.entretienId) === Number(cancelCalendarEntretien.id)) {
            setCancelCalendarEntretien(null);
        }
    }, [calendarResult, replanifyEntretien, cancelCalendarEntretien]);

    const handleEvaluerSave = (data) => {
        setCandidatures((prev) =>
            prev.map((c) =>
                c.id === selectedCandidature.id ? { ...c, evaluation: data } : c
            )
        );
    };

    const getStatusBadgeClass = (statut) => {
        const s = normalizeStatus(statut);
        const map = {
            "en attente": "tc-status-pending",
            "validée": "tc-status-approved",
            "rejetée": "tc-status-rejected",
            "annulée": "tc-status-cancelled",
        };
        return map[s] || "";
    };

    const getTechCount = (candidatureId) => {
        const list = entretiensByCandidature[candidatureId] || [];
        return list.filter((e) => e.type === "TECHNIQUE").length;
    };

    const niveauEtudeOptions = useMemo(() => {
        return [...new Set(candidatures.map((c) => getDisplayedNiveauEtude(c)).filter(Boolean))];
    }, [candidatures]);

    const etablissementOptions = useMemo(() => {
        return [...new Set(candidatures.map((c) => getDisplayedEtablissement(c)).filter(Boolean))];
    }, [candidatures]);

    const anneeExperienceOptions = useMemo(() => {
        return [...new Set(candidatures.map((c) => c.anneesExperience).filter(Boolean))];
    }, [candidatures]);

    const typeEmploiOptions = useMemo(() => {
        return [...new Set(candidatures.map((c) => c.typeEmploi).filter(Boolean))];
    }, [candidatures]);

    const tabs = [
        { label: "En attente", icon: Clock, count: getCandidaturesByTab(0).length },
        { label: "Approuvées", icon: ThumbsUp, count: getCandidaturesByTab(1).length },
        { label: "Rejetées / Annulées", icon: ThumbsDown, count: getCandidaturesByTab(2).length },
    ];

    const filteredData = filterCandidatures(getCandidaturesByTab(activeTab));
    const showTechnicalInterviewColumn = activeTab !== 0;
    const tableColSpan = showTechnicalInterviewColumn ? 9 : 8;

    return (
        <div className="tcPage">
            <div className="tcHeader">
                <div>
                    <h1>Traitement des Candidatures</h1>
                    <p>Gérez et suivez les candidatures reçues</p>
                </div>
            </div>

            <div className="tcTabs">
                {tabs.map((tab, index) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={index}
                            className={`tcTab ${activeTab === index ? "tcTab--active" : ""}`}
                            onClick={() => setActiveTab(index)}
                            type="button"
                        >
                            <Icon size={16} />
                            <span>{tab.label}</span>
                            <span className="tcTab__count">{tab.count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="tcWrap">
                <div className="tcCard">
                    <div className="tcBody">
                        <div className="tcFilterSection">
                            <div className="tcFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="tcFilterGrid">
                                <div className="tcField">
                                    <div className="tcField">
                                        <label>Campagne</label>
                                        <select
                                            value={filters.campagne}
                                            onChange={(e) => handleFilterChange("campagne", e.target.value)}
                                        >
                                            <option value="">Toutes</option>
                                            {(campagnes || []).map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.nom}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <label>Intitulé du poste</label>
                                    <input
                                        type="text"
                                        value={filters.poste}
                                        onChange={(e) => handleFilterChange("poste", e.target.value)}
                                        placeholder="Rechercher par poste..."
                                    />
                                </div>

                                <div className="tcField">
                                    <label>Niveau d'étude</label>
                                    <select
                                        value={filters.niveauEtude}
                                        onChange={(e) => handleFilterChange("niveauEtude", e.target.value)}
                                    >
                                        <option value="">Tous</option>
                                        {niveauEtudeOptions.map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="tcField">
                                    <label>Établissement</label>
                                    <select
                                        value={filters.etablissement}
                                        onChange={(e) => handleFilterChange("etablissement", e.target.value)}
                                    >
                                        <option value="">Tous</option>
                                        {etablissementOptions.map((e) => (
                                            <option key={e} value={e}>
                                                {e}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="tcField">
                                    <label>Années d'expérience</label>
                                    <select
                                        value={filters.anneeExperience}
                                        onChange={(e) => handleFilterChange("anneeExperience", e.target.value)}
                                    >
                                        <option value="">Tous</option>
                                        {anneeExperienceOptions.map((a) => (
                                            <option key={a} value={a}>
                                                {a}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="tcField">
                                    <label>Type d'emploi</label>
                                    <select
                                        value={filters.typeEmploi}
                                        onChange={(e) => handleFilterChange("typeEmploi", e.target.value)}
                                    >
                                        <option value="">Tous</option>
                                        {typeEmploiOptions.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="tcFilterActions">
                                <button
                                    className={`tcBtn ${isAnyFilterFilled ? "tcBtn--warning" : "tcBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tcWrap">
                <div className="tcCard">
                    <div className="tcBody">
                        <div className="tcTopRow">
                            <h2 className="tcTitle">
                                {tabs[activeTab].label} ({filteredData.length})
                            </h2>
                        </div>

                        <div className="tcTableWrap">
                            <table className="tcTable">
                                <thead>
                                <tr>
                                    <th>Campagne</th>
                                    <th>Candidat</th>
                                    <th>Poste</th>
                                    {/*<th>Établissement</th>*/}
                                    <th>Expérience</th>
                                    <th>Type emploi</th>
                                    {showTechnicalInterviewColumn && <th>Entretien tech.</th>}
                                    <th>Date</th>
                                    <th>Statut</th>
                                    <th className="is-center">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={tableColSpan} className="tcEmptyCell">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan={tableColSpan} className="tcEmptyCell">
                                            Aucune candidature trouvée
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((candidature) => {
                                        const canEvaluate = hasCurrentUserAsEvaluationIntervenant(candidature, currentUserId);
                                        const cvDocument = getCandidatureCvDocument(candidature);
                                        const hasCvCandidate = canResolveCandidateCv(candidature);

                                        return (
                                        <tr key={candidature.id}>
                                            <td>
                                                <div className="tcCellContent">
                                                  <span className="tcMuted">
                                                     {getCampagneName(candidature)}
                                                  </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="tcCellContent">
                                                    <div className="tcCandidatInfo">
                                                        <User size={16} className="tcCandidatIcon" />
                                                        <div className="tcCandidatText">
                                                            <span className="tcStrong">
                                                                {candidature.candidatPrenom} {candidature.candidatNom}
                                                            </span>
                                                            <span className="tcEmail">
                                                                {candidature.candidatEmail || "—"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tcCellContent">
                                                    <div className="tcInline">
                                                        <Briefcase size={14} className="tcMutedIcon" />
                                                        <span>{getDisplayedPoste(candidature)}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/*<td>*/}
                                            {/*    <div className="tcCellContent">*/}
                                            {/*        <div className="tcInline">*/}
                                            {/*            <Building2 size={14} className="tcMutedIcon" />*/}
                                            {/*            <span className="tcMuted">*/}
                                            {/*                {getDisplayedEtablissement(candidature)}*/}
                                            {/*            </span>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*</td>*/}

                                            <td>
                                                <div className="tcCellContent">
                                                    <span className="tcMuted">{candidature.anneesExperience || "—"}</span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tcCellContent">
                                                    <span className="tcBadge">{candidature.typeEmploi || "—"}</span>
                                                </div>
                                            </td>

                                            {showTechnicalInterviewColumn && (
                                                <td>
                                                    <div className="tcCellContent">
                                                        <span className="tcBadge">Tech: {getTechCount(candidature.id)}</span>
                                                    </div>
                                                </td>
                                            )}

                                            <td>
                                                <div className="tcCellContent">
                                                    <div className="tcInline">
                                                        <Calendar size={14} className="tcMutedIcon" />
                                                        <span className="tcMuted">
                                                            {formatDate(candidature.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tcCellContent">
                                                    <span className={`tcStatus ${getStatusBadgeClass(candidature.statut)}`}>
                                                        {getDisplayStatus(candidature.statut)}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="is-right">
                                                <div className="tcCellContent tcCellContent--right">
                                                    <div className="tcRowActions">
                                                        <button
                                                            className="tcIconBtn"
                                                            type="button"
                                                            title="Voir"
                                                            onClick={() => openView(candidature)}
                                                        >
                                                            <Eye size={16} />
                                                        </button>

                                                        <button
                                                            className={`tcIconBtn tcIconBtn--download ${!hasCvCandidate ? "tcIconBtn--disabled" : ""}`}
                                                            type="button"
                                                            title={hasCvCandidate ? "Telecharger le CV" : "Aucun CV disponible"}
                                                            aria-label={hasCvCandidate ? "Telecharger le CV" : "Aucun CV disponible"}
                                                            onClick={() => handleDownloadCv(candidature)}
                                                        >
                                                            <Download size={16} />
                                                        </button>

                                                        {activeTab === 0 && (
                                                            <>
                                                                <button
                                                                    className="tcIconBtn tcIconBtn--success"
                                                                    type="button"
                                                                    title="Sélectionner"
                                                                    onClick={() => openApprove(candidature)}
                                                                >
                                                                    <Check size={16} />
                                                                </button>

                                                                <button
                                                                    className="tcIconBtn tcIconBtn--danger"
                                                                    type="button"
                                                                    title="Rejeter"
                                                                    onClick={() => openReject(candidature)}
                                                                >
                                                                    <X size={16} />
                                                                </button>

                                                                <button
                                                                    className="tcIconBtn tcIconBtn--cancel"
                                                                    type="button"
                                                                    title="Annuler"
                                                                    onClick={() => openAnnuler(candidature)}
                                                                >
                                                                    <Ban size={16} />
                                                                </button>
                                                            </>
                                                        )}

                                                        {activeTab === 1 && (
                                                            <>
                                                                <button
                                                                    className="tcIconBtn tcIconBtn--calendar"
                                                                    type="button"
                                                                    title="Planifier entretien technique"
                                                                    onClick={() => openPlanifier(candidature)}
                                                                >
                                                                    <Calendar size={16} />
                                                                </button>

                                                                <button
                                                                    className="tcIconBtn"
                                                                    type="button"
                                                          title="Historique des entretiens"
                                                                    onClick={() => openHistorique(candidature)}
                                                                >
                                                                    <History size={16} />
                                                                </button>

                                                                <button
                                                                    className={`tcIconBtn tcIconBtn--star ${
                                                                        candidature.evaluation
                                                                            ? "tcIconBtn--star--done"
                                                                            : ""
                                                                    } ${
                                                                        !canEvaluate ? "tcIconBtn--disabled" : ""
                                                                    }`}
                                                                    type="button"
                                                                    title={
                                                                        !canEvaluate
                                                                            ? "Évaluation disponible uniquement pour les intervenants désignés"
                                                                            : candidature.evaluation
                                                                                ? "Évaluation enregistrée"
                                                                                : "Évaluer"
                                                                    }
                                                                    onClick={() => openEvaluer(candidature)}
                                                                    disabled={!canEvaluate}
                                                                >
                                                                    <Star size={16} />
                                                                </button>

                                                                <button
                                                                    className="tcIconBtn tcIconBtn--cancel"
                                                                    type="button"
                                                                    title="Annuler"
                                                                    onClick={() => openAnnuler(candidature)}
                                                                >
                                                                    <Ban size={16} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>

                        {!!error && (
                            <div className="tcWarning" style={{ marginTop: 10 }}>
                                {String(error)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                open={showViewModal}
                title={`Candidature — ${selectedCandidature?.candidatPrenom || ""} ${selectedCandidature?.candidatNom || ""}`}
                onClose={closeView}
                footer={
                    <button className="tcBtn tcBtn--primary" type="button" onClick={closeView}>
                        Fermer
                    </button>
                }
            >
                {selectedCandidature && (
                    <div className="tcViewContent">
                        <div className="tcViewRow">
                            <div className="tcViewLabel">Campagne :</div>
                            <div className="tcViewValue">
                                {getCampagneName(selectedCandidature)}
                            </div>
                        </div>
                        <div className="tcViewRow">
                            <div className="tcViewLabel">Candidat :</div>
                            <div className="tcViewValue">
                                {selectedCandidature.candidatPrenom} {selectedCandidature.candidatNom}
                            </div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Email :</div>
                            <div className="tcViewValue">{selectedCandidature.candidatEmail || "—"}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Téléphone :</div>
                            <div className="tcViewValue">{selectedCandidature.candidatPhone || "—"}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Poste :</div>
                            <div className="tcViewValue">{getDisplayedPoste(selectedCandidature)}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Profil recherché :</div>
                            <div className="tcViewValue">{selectedCandidature.profilRecherche || "—"}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Niveau d'étude :</div>
                            <div className="tcViewValue">{getDisplayedNiveauEtude(selectedCandidature)}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Établissement :</div>
                            <div className="tcViewValue">{getDisplayedEtablissement(selectedCandidature)}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Expérience :</div>
                            <div className="tcViewValue">{selectedCandidature.anneesExperience || "—"}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Type d'emploi :</div>
                            <div className="tcViewValue">{selectedCandidature.typeEmploi || "—"}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Date de postulation :</div>
                            <div className="tcViewValue">{formatDate(selectedCandidature.createdAt)}</div>
                        </div>

                        <div className="tcViewRow">
                            <div className="tcViewLabel">Statut :</div>
                            <div className="tcViewValue">
                                <span className={`tcStatus ${getStatusBadgeClass(selectedCandidature.statut)}`}>
                                    {getDisplayStatus(selectedCandidature.statut)}
                                </span>
                            </div>
                        </div>

                        {selectedCandidature.motifRejet && (
                            <div className="tcViewRow">
                                <div className="tcViewLabel">Motif de rejet :</div>
                                <div className="tcViewValue tcViewValue--cancel">
                                    {selectedCandidature.motifRejet}
                                </div>
                            </div>
                        )}

                        {selectedCandidature.motifAnnulation && (
                            <div className="tcViewRow">
                                <div className="tcViewLabel">Motif d'annulation :</div>
                                <div className="tcViewValue tcViewValue--cancel">
                                    {selectedCandidature.motifAnnulation}
                                </div>
                            </div>
                        )}

                        {selectedCandidature.evaluation && (
                            <div className="tcViewRow">
                                <div className="tcViewLabel">Évaluation :</div>
                                <div className="tcViewValue">
                                    <div className="tc-eval-summary">
                                        <span className="tc-eval-note">
                                            {"★".repeat(selectedCandidature.evaluation.note)}
                                            {"☆".repeat(5 - selectedCandidature.evaluation.note)} —{" "}
                                            {
                                                ["", "Insuffisant", "Passable", "Bien", "Très bien", "Excellent"][
                                                    selectedCandidature.evaluation.note
                                                    ]
                                            }
                                        </span>
                                        <span className="tc-eval-decision">
                                            {selectedCandidature.evaluation.decision}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                open={showApproveModal}
                title="Approuver la candidature"
                onClose={closeApprove}
                footer={
                    <>
                        <button className="tcBtn tcBtn--ghost" type="button" onClick={closeApprove}>
                            Annuler
                        </button>
                        <button className="tcBtn tcBtn--success" type="button" onClick={confirmApprove}>
                            Approuver
                        </button>
                    </>
                }
            >
                <div className="tcConfirmContent">
                    <p>
                        Voulez-vous vraiment approuver la candidature de{" "}
                        <strong>
                            {selectedCandidature?.candidatPrenom} {selectedCandidature?.candidatNom}
                        </strong>{" "}
                        pour le poste de <strong>{getDisplayedPoste(selectedCandidature)}</strong> ?
                    </p>
                </div>
            </Modal>

            <Modal
                open={showRejectModal}
                title="Rejeter la candidature"
                onClose={closeReject}
                footer={
                    <>
                        <button className="tcBtn tcBtn--ghost" type="button" onClick={closeReject}>
                            Annuler
                        </button>
                        <button className="tcBtn tcBtn--danger" type="button" onClick={confirmReject}>
                            Rejeter
                        </button>
                    </>
                }
            >
                <div className="tcRejectContent">
                    <p>
                        Vous êtes sur le point de rejeter la candidature de{" "}
                        <strong>
                            {selectedCandidature?.candidatPrenom} {selectedCandidature?.candidatNom}
                        </strong>.
                    </p>
                    <div className="tcField">
                        <label>Motif du rejet *</label>
                        <textarea
                            rows={4}
                            value={rejectMotif}
                            onChange={(e) => setRejectMotif(e.target.value)}
                            placeholder="Veuillez préciser le motif du rejet..."
                        />
                    </div>
                </div>
            </Modal>

            <AnnulerModal
                open={showAnnulerModal}
                onClose={closeAnnuler}
                candidature={selectedCandidature}
                onConfirm={confirmAnnuler}
            />

            <PlanifierEntretienModal
                open={showPlanifierModal}
                onClose={closePlanifier}
                candidature={selectedCandidature}
                onSave={handlePlanifierSave}
                users={users}
                currentUserId={currentUserId}
            />

            <HistoriqueEntretiensModal
                open={showHistoriqueModal}
                onClose={closeHistorique}
                candidature={selectedCandidature}
                entretiens={selectedCandidature ? (entretiensByCandidature[selectedCandidature.id] || []) : []}
                users={users}
                onCalendarAction={openCalendarModal}
            />

            <ReplanifierEntretienModal
                open={!!replanifyEntretien}
                onClose={closeCalendarModal}
                candidature={selectedCandidature}
                entretien={replanifyEntretien}
                users={users}
                currentUserId={currentUserId}
                onSubmit={handleInterviewCalendarSubmit}
                loading={entretiensState.loading}
            />

            <AnnulerEntretienCalendarModal
                open={!!cancelCalendarEntretien}
                onClose={closeCalendarModal}
                candidature={selectedCandidature}
                entretien={cancelCalendarEntretien}
                loading={entretiensState.loading}
                onConfirm={handleInterviewCalendarSubmit}
            />

            <EvaluerModal
                open={showEvaluerModal}
                onClose={closeEvaluer}
                candidature={selectedCandidature}
                onSave={handleEvaluerSave}
            />
        </div>
    );
}
